import type { FlexProps, HeadingProps, TextProps } from '@chakra-ui/react';
import { Flex, VisuallyHidden } from '@chakra-ui/react';
import { FormatPrice } from 'components/localisation/FormatPrice';
import { ProductPriceSaving } from 'components/product/ProductPriceSaving';
import type { Money } from 'types/product/Money';

interface PriceProps extends TextProps {
  subtotalAmount?: Money | null;
  totalAmount: Money;
  direction?: 'row' | 'row-reverse';
  size?: HeadingProps['size'];
  gap?: FlexProps['gap'];
  fontWeight?: HeadingProps['fontWeight'];
  isSavingVisible?: boolean;
  showSavingsAsAmount?: boolean;
}

export function Price({
  subtotalAmount,
  totalAmount,
  direction = 'row',
  size = 'title2',
  gap = '4xs',
  fontWeight = 'medium',
  isSavingVisible,
  showSavingsAsAmount,
  ...rest
}: PriceProps) {
  const isSubtotalDifferent = subtotalAmount?.amount !== totalAmount?.amount;
  const isFreeProduct =
    subtotalAmount?.amount === 0 && totalAmount.amount === 0;
  const isFreeByDiscount = isSubtotalDifferent && totalAmount.amount === 0;

  const totalPrice = isFreeByDiscount || isFreeProduct ? 0 : totalAmount.amount;

  return (
    <Flex gap={gap} flexDirection={direction} justifyContent="start">
      {(isFreeProduct || isFreeByDiscount || isSubtotalDifferent) && (
        <>
          <VisuallyHidden>Price</VisuallyHidden>
          <FormatPrice
            className="price"
            size={size}
            color="gray.600"
            fontWeight={fontWeight}
            amount={totalPrice}
            showFreeIfZero
          />
        </>
      )}

      {!isFreeProduct && !!subtotalAmount && (
        <>
          <>
            <VisuallyHidden>
              {isSubtotalDifferent ? 'Was' : 'Price'}
            </VisuallyHidden>
            <FormatPrice
              className={isSubtotalDifferent ? 'price--was' : 'price'}
              amount={subtotalAmount.amount}
              isCurrencyCodeHidden={isSubtotalDifferent}
              size={size}
              opacity={isSubtotalDifferent ? 0.36 : 1}
              color="gray.600"
              fontWeight={fontWeight}
              {...rest}
              hasStrikeThrough={isSubtotalDifferent}
            />
          </>
          {isSavingVisible && isSubtotalDifferent && (
            <ProductPriceSaving
              compareAtPrice={subtotalAmount}
              price={totalAmount}
              pb="2px"
              alignSelf="flex-end"
              showAsAmount={showSavingsAsAmount}
            />
          )}
        </>
      )}
    </Flex>
  );
}
